import React, { useContext, useEffect, useState, useCallback, useMemo } from "react";
import Context from "../../../Context";
import { apiFetch } from "../../../utils/api";
import { 
  ArrowUpRight, 
  RefreshCw, 
  Calendar, 
  Search, 
  X, 
  Filter,
  Wallet,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Tag,
  Check,
  TrendingUp,
  Target,
  PieChart as PieChartIcon,
  BarChart3,
  ChevronUp,
  AlertCircle,
  Edit3,
  Save
} from "lucide-react";
import { auth, db } from "../../../firebase/config";
import { doc, getDoc, setDoc } from "firebase/firestore";
import styles from "./Budget.module.scss";

interface Transaction {
  id: string;
  name: string;
  amount: number;
  date: string;
  datetime?: string;
  authorized_date?: string;
  category: string[] | null;
  merchant_name?: string;
  pending?: boolean;
  account_id: string;
  personal_finance_category?: {
    primary?: string;
    detailed?: string;
    confidence_level?: string;
  };
}

interface Account {
  account_id: string;
  name: string;
  official_name?: string;
  type: string;
  subtype?: string;
  mask?: string;
  balances: {
    current: number | null;
    available: number | null;
  };
}

interface CategorySpending {
  category: string;
  amount: number;
  transactionCount: number;
  percentage: number;
  limit?: number;
  isOverBudget?: boolean;
}

interface BudgetLimits {
  [category: string]: number;
}

interface BarChartData {
  label: string;
  amount: number;
  date: Date;
  categories?: { category: string; amount: number; percentage: number; color: string }[];
}

type TimeFilter = "week" | "month" | "3months" | "6months" | "all";

const Budget: React.FC = () => {
  const { accessToken, linkSuccess } = useContext(Context);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSync, setLastSync] = useState<string | null>(null);
  
  // Filter states
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("month");
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>(["all"]);
  const [tempSelectedAccounts, setTempSelectedAccounts] = useState<string[]>(["all"]);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [hideBalances, setHideBalances] = useState(false);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [useCustomDateRange, setUseCustomDateRange] = useState(false);
  
  // Budget specific states
  const [budgetLimits, setBudgetLimits] = useState<BudgetLimits>({});
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [editingLimit, setEditingLimit] = useState<string>("");
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [selectedCategoryModal, setSelectedCategoryModal] = useState<string | null>(null);
  const [periodOffset, setPeriodOffset] = useState(0); // For navigating periods
  const [chartType, setChartType] = useState<'bar' | 'pie'>('bar'); // Chart type selector
  const [hoveredBarIndex, setHoveredBarIndex] = useState<number | null>(null);
  const [selectedBarModal, setSelectedBarModal] = useState<number | null>(null);
  const [hoveredPieCategory, setHoveredPieCategory] = useState<string | null>(null);
  const [showAverageLine, setShowAverageLine] = useState(true); // Toggle for average line
  const [showCategorySegments, setShowCategorySegments] = useState(false); // Toggle for permanent segments

  // Color cache to ensure unique colors and consistency
  const categoryColorCache = useMemo(() => new Map<string, string>(), []);

  // Generate consistent, unique color for a category
  const getCategoryColorByName = useCallback((categoryName: string): string => {
    // Return cached color if exists
    if (categoryColorCache.has(categoryName)) {
      return categoryColorCache.get(categoryName)!;
    }

    // Create a hash from the category name
    let hash = 0;
    for (let i = 0; i < categoryName.length; i++) {
      const char = categoryName.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    
    const absHash = Math.abs(hash);
    
    // Golden ratio for even distribution
    const goldenRatioConjugate = 0.618033988749895;
    let hueBase = (absHash * goldenRatioConjugate) % 1.0;
    let hue = Math.floor(hueBase * 360);
    
    // Check for color conflicts and adjust if needed
    const minHueDifference = 25; // Minimum hue difference between colors
    const existingColors = Array.from(categoryColorCache.values());
    let attempts = 0;
    let colorFound = false;
    
    while (!colorFound && attempts < 360) {
      colorFound = true;
      
      for (const existingColor of existingColors) {
        // Extract hue from existing HSL color
        const hueMatch = existingColor.match(/hsl\((\d+),/);
        if (hueMatch) {
          const existingHue = parseInt(hueMatch[1]);
          const hueDiff = Math.min(
            Math.abs(hue - existingHue),
            360 - Math.abs(hue - existingHue)
          );
          
          if (hueDiff < minHueDifference) {
            // Adjust hue by golden ratio step to find next available color
            hue = (hue + Math.floor(360 * goldenRatioConjugate)) % 360;
            colorFound = false;
            break;
          }
        }
      }
      attempts++;
    }
    
    // Vary saturation and lightness for additional distinction
    const satLevels = [78, 88, 72, 82, 85, 75];
    const saturation = satLevels[absHash % satLevels.length];
    
    const lightLevels = [48, 42, 54, 46, 52, 44];
    const lightness = lightLevels[(absHash >> 8) % lightLevels.length];
    
    const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    categoryColorCache.set(categoryName, color);
    
    return color;
  }, [categoryColorCache]);

  useEffect(() => {
    if (linkSuccess && accessToken) {
      loadTransactionData();
      loadUserPreferences();
      loadBudgetLimits();
    }
  }, [linkSuccess, accessToken]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(`.${styles.customDropdown}`)) {
        setShowTimeDropdown(false);
        setShowAccountDropdown(false);
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load user preferences from Firestore
  const loadUserPreferences = async () => {
    try {
      if (!auth.currentUser) return;
      
      const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        if (data.preferences) {
          setHideBalances(data.preferences.hideBalances || false);
          if (data.preferences.selectedAccounts) {
            setSelectedAccounts(data.preferences.selectedAccounts);
            setTempSelectedAccounts(data.preferences.selectedAccounts);
          }
        }
      }
    } catch (error) {
      console.error("Error loading preferences:", error);
    }
  };

  // Load budget limits from Firestore
  const loadBudgetLimits = async () => {
    try {
      if (!auth.currentUser) return;
      
      const budgetDoc = await getDoc(doc(db, "users", auth.currentUser.uid, "budgetData", "limits"));
      if (budgetDoc.exists()) {
        setBudgetLimits(budgetDoc.data().limits || {});
      }
    } catch (error) {
      console.error("Error loading budget limits:", error);
    }
  };

  // Save budget limit for a category
  const saveBudgetLimit = async (category: string, limit: number) => {
    try {
      if (!auth.currentUser) return;
      
      const newLimits = { ...budgetLimits, [category]: limit };
      setBudgetLimits(newLimits);
      
      await setDoc(
        doc(db, "users", auth.currentUser.uid, "budgetData", "limits"),
        { limits: newLimits },
        { merge: true }
      );
      
      setEditingCategory(null);
      setEditingLimit("");
    } catch (error) {
      console.error("Error saving budget limit:", error);
    }
  };

  // Toggle hide balances
  const toggleHideBalances = async () => {
    const newValue = !hideBalances;
    setHideBalances(newValue);
    
    try {
      if (!auth.currentUser) return;
      
      const userRef = doc(db, "users", auth.currentUser.uid);
      await setDoc(userRef, {
        preferences: {
          hideBalances: newValue,
          selectedAccounts: selectedAccounts
        }
      }, { merge: true });
    } catch (error) {
      console.error("Error saving preference:", error);
    }
  };

  // Toggle account selection
  const toggleAccountSelection = (accountId: string) => {
    if (accountId === "all") {
      setTempSelectedAccounts(["all"]);
    } else {
      setTempSelectedAccounts(prev => {
        const withoutAll = prev.filter(id => id !== "all");
        if (withoutAll.includes(accountId)) {
          const updated = withoutAll.filter(id => id !== accountId);
          return updated.length === 0 ? ["all"] : updated;
        } else {
          return [...withoutAll, accountId];
        }
      });
    }
  };

  // Apply account selection
  const applyAccountSelection = async () => {
    setSelectedAccounts(tempSelectedAccounts);
    setShowAccountDropdown(false);
    
    try {
      if (!auth.currentUser) return;
      
      const userRef = doc(db, "users", auth.currentUser.uid);
      await setDoc(userRef, {
        preferences: {
          hideBalances: hideBalances,
          selectedAccounts: tempSelectedAccounts
        }
      }, { merge: true });
    } catch (error) {
      console.error("Error saving preference:", error);
    }
  };

  // Handle "All Accounts" selection
  const handleAllAccountsSelection = async () => {
    setSelectedAccounts(["all"]);
    setTempSelectedAccounts(["all"]);
    setShowAccountDropdown(false);
    
    try {
      if (!auth.currentUser) return;
      
      const userRef = doc(db, "users", auth.currentUser.uid);
      await setDoc(userRef, {
        preferences: {
          hideBalances: hideBalances,
          selectedAccounts: ["all"]
        }
      }, { merge: true });
    } catch (error) {
      console.error("Error saving preference:", error);
    }
  };

  // Format category name
  const formatCategoryName = (category: string): string => {
    return category.replace(/_/g, ' ');
  };

  // Get unique categories from transactions
  const getUniqueCategories = (): string[] => {
    const categoriesSet = new Set<string>();
    transactions.forEach(t => {
      if (t.amount > 0) { // Only spending
        const category = t.personal_finance_category?.primary || t.category?.[0] || "Uncategorized";
        categoriesSet.add(category);
      }
    });
    return Array.from(categoriesSet).sort();
  };

  // Get unique accounts that have transactions
  const getUniqueTransactionAccounts = (): Account[] => {
    const accountIds = new Set<string>();
    transactions.forEach(t => {
      accountIds.add(t.account_id);
    });
    return accounts.filter(acc => accountIds.has(acc.account_id));
  };

  // Get date range based on time filter with offset
  const getDateRange = useCallback((filter: TimeFilter, offset: number = 0): { start: Date; end: Date } => {
    const now = new Date();
    let end = new Date(now);
    let start = new Date(now);

    switch (filter) {
      case "week":
        end.setDate(now.getDate() - (offset * 7));
        end.setHours(23, 59, 59, 999);
        start = new Date(end);
        start.setDate(end.getDate() - 6);
        start.setHours(0, 0, 0, 0);
        break;
      case "month":
        end.setMonth(now.getMonth() - offset);
        end.setDate(new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate());
        end.setHours(23, 59, 59, 999);
        start = new Date(end.getFullYear(), end.getMonth(), 1, 0, 0, 0, 0);
        break;
      case "3months":
        end.setMonth(now.getMonth() - (offset * 3));
        end.setHours(23, 59, 59, 999);
        start = new Date(end);
        start.setMonth(end.getMonth() - 2);
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        break;
      case "6months":
        end.setMonth(now.getMonth() - (offset * 6));
        end.setHours(23, 59, 59, 999);
        start = new Date(end);
        start.setMonth(end.getMonth() - 5);
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        break;
      case "all":
        start.setMonth(now.getMonth() - 24);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
    }

    return { start, end };
  }, []);

  // Get period label for navigation
  const getPeriodLabel = (): string => {
    if (useCustomDateRange && customStartDate && customEndDate) {
      return `${new Date(customStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(customEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    
    const { start, end } = getDateRange(timeFilter, periodOffset);
    
    switch (timeFilter) {
      case "week":
        return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      case "month":
        return start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      case "3months":
        return `${start.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
      case "6months":
        return `${start.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
      case "all":
        return "All Time";
    }
  };

  // Filter transactions based on current filters
  const filteredTransactions = useMemo(() => {
    let filtered = transactions.filter(t => t.amount > 0); // Only spending

    // Time filter
    if (useCustomDateRange && customStartDate && customEndDate) {
      const [startYear, startMonth, startDay] = customStartDate.split('-').map(Number);
      const start = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);
      const [endYear, endMonth, endDay] = customEndDate.split('-').map(Number);
      const end = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);
      
      filtered = filtered.filter(t => {
        const dateToUse = t.datetime || t.authorized_date || t.date;
        const txnDateStr = dateToUse.split('T')[0];
        const [year, month, day] = txnDateStr.split('-').map(Number);
        const txnDate = new Date(year, month - 1, day, 12, 0, 0, 0);
        return txnDate >= start && txnDate <= end;
      });
    } else {
      const { start, end } = getDateRange(timeFilter, periodOffset);
      filtered = filtered.filter(t => {
        const dateToUse = t.datetime || t.authorized_date || t.date;
        const txnDateStr = dateToUse.split('T')[0];
        const [year, month, day] = txnDateStr.split('-').map(Number);
        const txnDate = new Date(year, month - 1, day, 12, 0, 0, 0);
        return txnDate >= start && txnDate <= end;
      });
    }

    // Account filter
    if (!selectedAccounts.includes("all")) {
      filtered = filtered.filter(t => selectedAccounts.includes(t.account_id));
    }

    // Category filter
    if (selectedCategory !== "all") {
      filtered = filtered.filter(t => {
        const category = t.personal_finance_category?.primary || t.category?.[0] || "Uncategorized";
        return category === selectedCategory;
      });
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t => 
        t.name.toLowerCase().includes(query) ||
        t.merchant_name?.toLowerCase().includes(query) ||
        t.category?.some(c => c.toLowerCase().includes(query)) ||
        t.personal_finance_category?.primary?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [transactions, timeFilter, periodOffset, selectedAccounts, selectedCategory, searchQuery, useCustomDateRange, customStartDate, customEndDate, getDateRange]);

  // Calculate total spending
  const totalSpending = useMemo(() => {
    return filteredTransactions.reduce((sum, t) => sum + t.amount, 0);
  }, [filteredTransactions]);

  // Calculate average spending (per day for week/month, per month for longer periods)
  const averageSpending = useMemo(() => {
    if (filteredTransactions.length === 0) return { amount: 0, period: 'day' };
    
    const { start, end } = useCustomDateRange && customStartDate && customEndDate
      ? { start: new Date(customStartDate), end: new Date(customEndDate) }
      : getDateRange(timeFilter, periodOffset);
    
    const days = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
    
    // Use per day for week and month, per month for longer periods
    if (timeFilter === 'week' || timeFilter === 'month') {
      return { amount: totalSpending / days, period: 'day' };
    } else {
      const months = Math.max(1, days / 30);
      return { amount: totalSpending / months, period: 'month' };
    }
  }, [filteredTransactions, totalSpending, timeFilter, periodOffset, useCustomDateRange, customStartDate, customEndDate, getDateRange]);

  // Calculate category spending
  const categorySpending = useMemo((): CategorySpending[] => {
    const categoryMap = new Map<string, { amount: number; count: number }>();
    
    filteredTransactions.forEach(t => {
      const category = t.personal_finance_category?.primary || t.category?.[0] || "Uncategorized";
      const existing = categoryMap.get(category) || { amount: 0, count: 0 };
      categoryMap.set(category, {
        amount: existing.amount + t.amount,
        count: existing.count + 1
      });
    });

    const result: CategorySpending[] = [];
    categoryMap.forEach((value, category) => {
      const limit = budgetLimits[category];
      result.push({
        category,
        amount: value.amount,
        transactionCount: value.count,
        percentage: totalSpending > 0 ? (value.amount / totalSpending) * 100 : 0,
        limit,
        isOverBudget: limit ? value.amount > limit : false
      });
    });

    return result.sort((a, b) => b.amount - a.amount);
  }, [filteredTransactions, totalSpending, budgetLimits]);

  // Get top 2 categories (for 3 column grid: 1 total + 2 categories)
  const topCategories = useMemo(() => {
    return categorySpending.slice(0, 2);
  }, [categorySpending]);

  // Generate bar chart data
  const barChartData = useMemo((): BarChartData[] => {
    if (filteredTransactions.length === 0) return [];

    const { start, end } = useCustomDateRange && customStartDate && customEndDate
      ? { start: new Date(customStartDate), end: new Date(customEndDate) }
      : getDateRange(timeFilter, periodOffset);

    const dataMap = new Map<string, { amount: number; date: Date }>();
    
    // Determine grouping based on time filter
    let groupBy: 'day' | 'week' | 'month' = 'day';
    if (timeFilter === '6months' || timeFilter === 'all') {
      groupBy = 'month';
    } else if (timeFilter === '3months') {
      groupBy = 'week';
    } else if (timeFilter === 'month') {
      groupBy = 'week';
    }

    // Initialize all periods with 0 (only up to today)
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const current = new Date(start);
    while (current <= end && current < today) {
      let key: string;
      if (groupBy === 'month') {
        key = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
        current.setMonth(current.getMonth() + 1);
      } else if (groupBy === 'week') {
        const weekStart = new Date(current);
        key = weekStart.toISOString().split('T')[0];
        current.setDate(current.getDate() + 7);
      } else {
        key = current.toISOString().split('T')[0];
        current.setDate(current.getDate() + 1);
      }
      if (!dataMap.has(key)) {
        dataMap.set(key, { amount: 0, date: new Date(current) });
      }
    }
    
    // Add today if not already included
    const todayKey = today.toISOString().split('T')[0];
    if (groupBy === 'day' && !dataMap.has(todayKey)) {
      dataMap.set(todayKey, { amount: 0, date: new Date(today) });
    }

    // Sum transactions with category breakdown
    const categoryBreakdownMap = new Map<string, Map<string, number>>();
    
    filteredTransactions.forEach(t => {
      const dateStr = t.datetime || t.authorized_date || t.date;
      const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
      const txnDate = new Date(year, month - 1, day);
      
      let key: string;
      if (groupBy === 'month') {
        key = `${txnDate.getFullYear()}-${String(txnDate.getMonth() + 1).padStart(2, '0')}`;
      } else if (groupBy === 'week') {
        const weekStart = new Date(txnDate);
        weekStart.setDate(txnDate.getDate() - txnDate.getDay());
        key = weekStart.toISOString().split('T')[0];
      } else {
        key = txnDate.toISOString().split('T')[0];
      }

      const existing = dataMap.get(key) || { amount: 0, date: txnDate };
      dataMap.set(key, { amount: existing.amount + t.amount, date: txnDate });
      
      // Track category breakdown
      const category = t.personal_finance_category?.primary || t.category?.[0] || "Uncategorized";
      if (!categoryBreakdownMap.has(key)) {
        categoryBreakdownMap.set(key, new Map());
      }
      const categoryMap = categoryBreakdownMap.get(key)!;
      categoryMap.set(category, (categoryMap.get(category) || 0) + t.amount);
    });

    // Convert to array and format labels
    const result: BarChartData[] = [];
    const sortedKeys = Array.from(dataMap.keys()).sort();
    
    sortedKeys.forEach(key => {
      const data = dataMap.get(key)!;
      let label: string;
      
      if (groupBy === 'month') {
        const [y, m] = key.split('-').map(Number);
        label = new Date(y, m - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      } else if (groupBy === 'week') {
        const weekStart = new Date(key);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        
        // Don't show dates beyond today
        if (weekEnd > today) {
          weekEnd.setTime(today.getTime());
        }
        
        const startStr = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const endStr = weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        label = `${startStr} - ${endStr}`;
      } else {
        const date = new Date(key);
        label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      }
      
      // Add category breakdown
      const categories: { category: string; amount: number; percentage: number; color: string }[] = [];
      const categoryMap = categoryBreakdownMap.get(key);
      if (categoryMap) {
        const sortedCategories = Array.from(categoryMap.entries()).sort((a, b) => b[1] - a[1]);
        sortedCategories.forEach(([category, amount]) => {
          categories.push({
            category,
            amount,
            percentage: (amount / data.amount) * 100,
            color: getCategoryColorByName(category)
          });
        });
      }
      
      result.push({ label, amount: data.amount, date: data.date, categories });
    });

    return result;
  }, [filteredTransactions, timeFilter, periodOffset, useCustomDateRange, customStartDate, customEndDate, getDateRange, getCategoryColorByName]);

  // Get max bar value for scaling
  const maxBarValue = useMemo(() => {
    return Math.max(...barChartData.map(d => d.amount), 1);
  }, [barChartData]);

  // Load transaction data from cache
  const loadTransactionData = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      if (!auth.currentUser) {
        throw new Error("Not authenticated");
      }

      // Load from transaction cache
      const cacheDoc = await getDoc(doc(db, "users", auth.currentUser.uid, "transactionCache", "latest"));
      
      if (cacheDoc.exists()) {
        const data = cacheDoc.data();
        setTransactions(data.transactions || []);
        setAccounts(data.accounts || []);
        setLastSync(data.lastSync);
      }

      if (forceRefresh || !cacheDoc.exists()) {
        // Fetch fresh data
        const txnResponse = await apiFetch('/api/transactions', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });

        if (txnResponse.ok) {
          const txnData = await txnResponse.json();
          setTransactions(txnData.latest_transactions || []);
        }

        const acctResponse = await apiFetch('/api/accounts', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });

        if (acctResponse.ok) {
          const acctData = await acctResponse.json();
          setAccounts(acctData.accounts || []);
        }

        setLastSync(new Date().toISOString());
      }
    } catch (error) {
      console.error("Error loading data:", error);
      setError(error instanceof Error ? error.message : "Failed to load data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadTransactionData(true);
    setRefreshing(false);
  };

  // Get transactions for a specific category
  const getCategoryTransactions = (category: string): Transaction[] => {
    return filteredTransactions.filter(t => {
      const txnCategory = t.personal_finance_category?.primary || t.category?.[0] || "Uncategorized";
      return txnCategory === category;
    }).sort((a, b) => {
      const dateA = new Date(a.datetime || a.authorized_date || a.date);
      const dateB = new Date(b.datetime || b.authorized_date || b.date);
      return dateB.getTime() - dateA.getTime();
    });
  };

  // Toggle category expansion
  const openCategoryModal = (category: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedCategoryModal(category);
  };

  const closeCategoryModal = () => {
    setSelectedCategoryModal(null);
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Math.abs(amount));
  };

  // Get color for category - kept for backwards compatibility
  const getCategoryColor = (categoryName: string): string => {
    return getCategoryColorByName(categoryName);
  };

  // Get account color
  const getAccountColorById = (accountId: string): string => {
    const accountIndex = accounts.findIndex(acc => acc.account_id === accountId);
    return `color${accountIndex >= 0 ? accountIndex % 6 : 0}`;
  };

  // Time filter options
  const timeFilterOptions: { value: TimeFilter; label: string }[] = [
    { value: "week", label: "This Week" },
    { value: "month", label: "This Month" },
    { value: "3months", label: "Last 3 Months" },
    { value: "6months", label: "Last 6 Months" },
    { value: "all", label: "All Time" }
  ];

  // Navigate periods
  const canGoForward = periodOffset > 0;
  const canGoBack = timeFilter !== "all";

  const goToPreviousPeriod = () => {
    if (canGoBack) {
      setPeriodOffset(prev => prev + 1);
    }
  };

  const goToNextPeriod = () => {
    if (canGoForward) {
      setPeriodOffset(prev => prev - 1);
    }
  };

  if (loading) {
    return (
      <div className={styles.budget}>
        <div className={styles.loadingState}>
          <RefreshCw className={styles.spinner} size={32} />
          <p>Loading budget data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.budget}>
        <div className={styles.errorState}>
          <p className={styles.errorMessage}>{error}</p>
          <button onClick={() => loadTransactionData(true)} className={styles.retryBtn}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.budget}>
      {/* Header */}
      <div className={styles.budgetHeader}>
        <h2>Budget Overview</h2>
        <button onClick={toggleHideBalances} className={styles.toggleBalanceBtn} title={hideBalances ? "Show balances" : "Hide balances"}>
          {hideBalances ? <EyeOff size={20} /> : <Eye size={20} />}
          <span>{hideBalances ? "Show" : "Hide"} Amounts</span>
        </button>
      </div>

      {/* Summary Cards */}
      <div className={styles.summaryGrid}>
        {/* Total Spending Card */}
        <div className={`${styles.summaryCard} ${styles.totalCard}`}>
          <div className={styles.cardIcon}>
            <ArrowUpRight size={24} />
          </div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>Total Spent</span>
            <span className={styles.cardValue}>
              {hideBalances ? "••••••" : formatCurrency(totalSpending)}
            </span>
            <span className={styles.cardSubtext}>
              Avg {hideBalances ? "••••" : formatCurrency(averageSpending.amount)}/{averageSpending.period}
            </span>
          </div>
        </div>

        {/* Top 2 Categories */}
        {topCategories.map((cat, index) => (
          <div 
            key={cat.category} 
            className={`${styles.summaryCard} ${styles.categoryCard} ${cat.isOverBudget ? styles.overBudget : ''}`}
          >
            <div className={styles.cardIcon} style={{ background: `linear-gradient(135deg, ${getCategoryColor(cat.category)}40 0%, ${getCategoryColor(cat.category)}20 100%)` }}>
              <Tag size={24} style={{ color: getCategoryColor(cat.category) }} />
            </div>
            <div className={styles.cardContent}>
              <span className={styles.cardLabel}>{formatCategoryName(cat.category)}</span>
              <span className={styles.cardValue}>
                {hideBalances ? "••••••" : formatCurrency(cat.amount)}
              </span>
              {cat.limit && (
                <span className={`${styles.cardSubtext} ${cat.isOverBudget ? styles.overBudgetText : ''}`}>
                  {cat.isOverBudget ? (
                    <>
                      <AlertCircle size={12} />
                      Over by {hideBalances ? "••••" : formatCurrency(cat.amount - cat.limit)}
                    </>
                  ) : (
                    `${hideBalances ? "••••" : formatCurrency(cat.limit - cat.amount)} remaining`
                  )}
                </span>
              )}
              <span className={styles.cardSubtext}>
                {cat.percentage.toFixed(1)}% of total
              </span>
            </div>
            {cat.isOverBudget && (
              <div className={styles.overBudgetBadge}>
                <AlertCircle size={14} />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Expand/Collapse All Categories */}
      {categorySpending.length > 0 && (
        <button 
          className={styles.expandCategoriesBtn}
          onClick={() => setShowAllCategories(!showAllCategories)}
        >
          {showAllCategories ? (
            <>
              <ChevronUp size={18} />
              Hide All Categories
            </>
          ) : (
            <>
              <ChevronDown size={18} />
              View All Categories ({categorySpending.length} total)
            </>
          )}
        </button>
      )}

      {/* All Categories Expanded */}
      {showAllCategories && (
        <div className={styles.allCategoriesGrid}>
          {categorySpending.map((cat, index) => {
            return (
            <div 
              key={cat.category} 
              className={`${styles.categoryItem} ${cat.isOverBudget ? styles.overBudget : ''}`}
              onClick={(e) => openCategoryModal(cat.category, e)}
              style={{ cursor: 'pointer' }}
            >
              <div className={styles.categoryInfo}>
                <div 
                  className={styles.categoryDot} 
                  style={{ background: getCategoryColor(cat.category) }}
                />
                <span className={styles.categoryName}>{formatCategoryName(cat.category)}</span>
                <span className={styles.categoryAmount}>
                  {hideBalances ? "••••" : formatCurrency(cat.amount)}
                </span>
                <ChevronRight size={18} />
              </div>
              <div className={styles.categoryMeta}>
                <span>{cat.transactionCount} transactions</span>
                <span>{cat.percentage.toFixed(1)}% of total</span>
              </div>
              <div className={styles.limitSection}>
                {editingCategory === cat.category ? (
                  <div className={styles.limitEdit}>
                    <input
                      type="number"
                      value={editingLimit}
                      onChange={(e) => setEditingLimit(e.target.value)}
                      placeholder="Set monthly limit"
                      className={styles.limitInput}
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    />
                    <button 
                      className={styles.saveBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        saveBudgetLimit(cat.category, parseFloat(editingLimit) || 0);
                      }}
                    >
                      <Save size={14} />
                    </button>
                    <button 
                      className={styles.cancelBtn}
                      onClick={(e) => { 
                        e.stopPropagation();
                        setEditingCategory(null); 
                        setEditingLimit(""); 
                      }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <button 
                    className={styles.setLimitBtn}
                    onClick={(e) => { 
                      e.stopPropagation();
                      setEditingCategory(cat.category); 
                      setEditingLimit(cat.limit?.toString() || ""); 
                    }}
                  >
                    <Target size={14} />
                    {cat.limit ? `Limit: ${formatCurrency(cat.limit)}` : "Set Limit"}
                    <Edit3 size={12} />
                  </button>
                )}
              </div>
              {cat.limit && (
                <div className={styles.progressBar}>
                  <div 
                    className={`${styles.progressFill} ${cat.isOverBudget ? styles.over : ''}`}
                    style={{ width: `${Math.min((cat.amount / cat.limit) * 100, 100)}%` }}
                  />
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* Filter Section */}
      <div className={styles.filterSection}>
        <div className={styles.mainFilters}>
          {/* Chart Type Selector */}
          <div className={styles.chartTypeSelector}>
            <button
              className={`${styles.chartTypeBtn} ${chartType === 'bar' ? styles.active : ''}`}
              onClick={() => setChartType('bar')}
            >
              <BarChart3 size={18} />
              Bar Chart
            </button>
            <button
              className={`${styles.chartTypeBtn} ${chartType === 'pie' ? styles.active : ''}`}
              onClick={() => setChartType('pie')}
            >
              <PieChartIcon size={18} />
              Pie Chart
            </button>
          </div>

          {/* Period Navigation */}
          <div className={styles.periodNavigation}>
            <button 
              className={styles.navArrow} 
              onClick={goToPreviousPeriod}
              disabled={!canGoBack}
            >
              <ChevronLeft size={20} />
            </button>
            <span className={styles.periodLabel}>{getPeriodLabel()}</span>
            <button 
              className={styles.navArrow} 
              onClick={goToNextPeriod}
              disabled={!canGoForward}
            >
              <ChevronRight size={20} />
            </button>
          </div>

          {/* Time Filter Dropdown */}
          <div className={styles.customDropdown}>
            <button 
              className={styles.dropdownTrigger}
              onClick={() => {
                setShowTimeDropdown(!showTimeDropdown);
                setShowAccountDropdown(false);
                setShowCategoryDropdown(false);
              }}
            >
              <Calendar size={20} />
              <span>{timeFilterOptions.find(o => o.value === timeFilter)?.label}</span>
              <ChevronDown size={18} className={showTimeDropdown ? styles.rotated : ''} />
            </button>
            
            {showTimeDropdown && (
              <div className={styles.dropdownMenu}>
                {timeFilterOptions.map(opt => (
                  <button
                    key={opt.value}
                    className={`${styles.dropdownItem} ${timeFilter === opt.value ? styles.active : ''}`}
                    onClick={() => {
                      setTimeFilter(opt.value);
                      setPeriodOffset(0);
                      setShowTimeDropdown(false);
                    }}
                  >
                    <Calendar size={20} />
                    <span>{opt.label}</span>
                    {timeFilter === opt.value && <span className={styles.checkmark}><Check size={16} /></span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Category Filter */}
          <div className={styles.customDropdown}>
            <button 
              className={`${styles.dropdownTrigger} ${styles.categoryTrigger}`}
              onClick={() => {
                setShowCategoryDropdown(!showCategoryDropdown);
                setShowTimeDropdown(false);
                setShowAccountDropdown(false);
              }}
            >
              <Tag size={20} />
              <span>
                {selectedCategory === "all" ? "All Categories" : formatCategoryName(selectedCategory)}
              </span>
              <ChevronDown size={18} className={showCategoryDropdown ? styles.rotated : ''} />
            </button>
            
            {showCategoryDropdown && (
              <div className={styles.dropdownMenu}>
                <button
                  className={`${styles.dropdownItem} ${selectedCategory === "all" ? styles.active : ''}`}
                  onClick={() => {
                    setSelectedCategory("all");
                    setShowCategoryDropdown(false);
                  }}
                >
                  <Tag size={18} />
                  <span>All Categories</span>
                  {selectedCategory === "all" && <span className={styles.checkmark}><Check size={16} /></span>}
                </button>
                {getUniqueCategories().map(cat => (
                  <button
                    key={cat}
                    className={`${styles.dropdownItem} ${selectedCategory === cat ? styles.active : ''}`}
                    onClick={() => {
                      setSelectedCategory(cat);
                      setShowCategoryDropdown(false);
                    }}
                  >
                    <Tag size={18} />
                    <span>{formatCategoryName(cat)}</span>
                    {selectedCategory === cat && <span className={styles.checkmark}><Check size={16} /></span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Account Filter */}
          {accounts.length > 1 && (
            <div className={styles.customDropdown}>
              <button 
                className={`${styles.dropdownTrigger} ${styles.accountTrigger}`}
                onClick={() => {
                  setShowAccountDropdown(!showAccountDropdown);
                  setShowTimeDropdown(false);
                  setShowCategoryDropdown(false);
                  if (!showAccountDropdown) {
                    setTempSelectedAccounts(selectedAccounts);
                  }
                }}
              >
                <Wallet size={20} />
                <span>
                  {selectedAccounts.includes("all") 
                    ? "All Accounts" 
                    : selectedAccounts.length === 1
                      ? accounts.find(a => a.account_id === selectedAccounts[0])?.name || "Select Account"
                      : `${selectedAccounts.length} Accounts`}
                </span>
                <ChevronDown size={18} className={showAccountDropdown ? styles.rotated : ''} />
              </button>
              
              {showAccountDropdown && (
                <div className={styles.dropdownMenu}>
                  <button
                    className={`${styles.dropdownItem} ${tempSelectedAccounts.includes("all") ? styles.active : ''}`}
                    onClick={handleAllAccountsSelection}
                  >
                    <Wallet size={18} />
                    <span>All Accounts</span>
                    {tempSelectedAccounts.includes("all") && <span className={styles.checkmark}><Check size={16} /></span>}
                  </button>
                  {getUniqueTransactionAccounts().map(acc => {
                    const colorClass = getAccountColorById(acc.account_id);
                    return (
                      <div
                        key={acc.account_id}
                        className={styles.accountItemWithCheckbox}
                        onClick={() => toggleAccountSelection(acc.account_id)}
                      >
                        <div className={`${styles.accountCheckbox} ${tempSelectedAccounts.includes(acc.account_id) ? styles.checked : ''}`}>
                          {tempSelectedAccounts.includes(acc.account_id) && <Check size={14} />}
                        </div>
                        <span className={`${styles.accountDropdownBadge} ${styles[colorClass]}`}>
                          {acc.name} {acc.mask ? `(••••${acc.mask})` : ''}
                        </span>
                      </div>
                    );
                  })}
                  {!tempSelectedAccounts.includes("all") && tempSelectedAccounts.length > 0 && (
                    <button
                      className={`${styles.dropdownItem} ${styles.confirmButton}`}
                      onClick={applyAccountSelection}
                    >
                      <span>Apply Selection</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className={styles.filterActions}>
            <button 
              className={styles.filterToggle}
              onClick={() => setShowFilters(!showFilters)}
              title="Toggle advanced filters"
            >
              <Filter size={18} />
            </button>
            <button 
              className={styles.refreshBtn} 
              onClick={handleRefresh}
              disabled={refreshing}
              title="Refresh data"
            >
              <RefreshCw size={18} className={refreshing ? styles.spinning : ""} />
            </button>
          </div>
        </div>

        {/* Expanded Filters */}
        {showFilters && (
          <div className={styles.expandedFilters}>
            <div className={styles.searchWrapper}>
              <Search size={22} />
              <input
                type="text"
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery("")} className={styles.clearSearch}>
                  <X size={18} />
                </button>
              )}
            </div>

            <div className={styles.dateRangeWrapper}>
              <div className={styles.dateInputs}>
                <div className={styles.dateInputGroup}>
                  <label>From</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className={styles.dateInput}
                  />
                </div>
                <div className={styles.dateInputGroup}>
                  <label>To</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className={styles.dateInput}
                  />
                </div>
                <button 
                  className={styles.applyDateRangeBtn}
                  onClick={() => setUseCustomDateRange(true)}
                  disabled={!customStartDate || !customEndDate}
                >
                  <Search size={18} />
                  Apply
                </button>
                {useCustomDateRange && (
                  <button 
                    className={styles.clearDateRangeBtn}
                    onClick={() => {
                      setUseCustomDateRange(false);
                      setCustomStartDate("");
                      setCustomEndDate("");
                    }}
                  >
                    <X size={18} />
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bar Chart */}
      {chartType === 'bar' && (
      <div className={styles.chartSection}>
        <div className={styles.chartHeader}>
          <div className={styles.chartTitle}>
            <BarChart3 size={20} />
            <span>Spending Over Time</span>
          </div>
          {barChartData.length > 0 && (() => {
            // Extract start date from first bar label and end date from last bar label
            const firstLabel = barChartData[0].label;
            const lastLabel = barChartData[barChartData.length - 1].label;
            
            // Parse the date range labels
            // For week view: "Nov 29 - Dec 5"
            // For day view: "Dec 19, 2025"
            let startDate: string;
            let endDate: string;
            
            if (firstLabel.includes(' - ')) {
              // Week view
              startDate = firstLabel.split(' - ')[0];
              endDate = lastLabel.split(' - ')[1] || lastLabel.split(' - ')[0];
            } else {
              // Day or month view - extract just month and day
              const formatShort = (dateStr: string) => {
                const parts = dateStr.split(',');
                return parts[0]; // "Dec 19" from "Dec 19, 2025"
              };
              startDate = formatShort(firstLabel);
              endDate = formatShort(lastLabel);
            }
            
            return (
              <div className={styles.chartDateRange}>
                <Calendar size={14} />
                <span>{startDate} - {endDate}</span>
              </div>
            );
          })()}
          <div className={styles.chartControls}>
            <button
              className={`${styles.chartControlBtn} ${showAverageLine ? styles.active : ''}`}
              onClick={() => setShowAverageLine(!showAverageLine)}
              title="Toggle average line"
            >
              <TrendingUp size={16} />
              <span>Average</span>
            </button>
            <button
              className={`${styles.chartControlBtn} ${showCategorySegments ? styles.active : ''}`}
              onClick={() => setShowCategorySegments(!showCategorySegments)}
              title="Toggle category breakdown"
            >
              <PieChartIcon size={16} />
              <span>Categories</span>
            </button>
          </div>
        </div>
        
        <div className={styles.barChart}>
          {barChartData.length === 0 ? (
            <div className={styles.noData}>
              <p>No spending data for this period</p>
            </div>
          ) : (() => {
            const average = barChartData.reduce((sum, d) => sum + d.amount, 0) / barChartData.length;
            const yAxisSteps = 5;
            const stepValue = maxBarValue / yAxisSteps;
            const averagePercent = (average / maxBarValue) * 100;
            
            return (
              <div className={styles.barChartWrapper}>
                {/* Y-Axis Labels */}
                <div className={styles.yAxis}>
                  {Array.from({ length: yAxisSteps + 1 }).map((_, i) => {
                    const value = maxBarValue - (i * stepValue);
                    const isNearAverage = Math.abs(value - average) < stepValue * 0.15;
                    
                    if (isNearAverage) {
                      return <div key={i} className={styles.yAxisLabel} />;
                    }
                    
                    return (
                      <div key={i} className={styles.yAxisLabel}>
                        {hideBalances ? "••" : formatCurrency(value)}
                      </div>
                    );
                  })}
                  {/* Average label on Y-axis */}
                  {showAverageLine && (
                    <div 
                      className={styles.yAxisAverage}
                      style={{ 
                        top: averagePercent > 85 ? 'auto' : undefined,
                        bottom: averagePercent > 85 ? undefined : `calc(${averagePercent}% + 35px)`
                      }}
                    >
                      {hideBalances ? "••" : formatCurrency(average)}
                      <span className={styles.avgText}>avg</span>
                    </div>
                  )}
                </div>
                
                <div className={styles.chartArea}>
                  {/* Grid Lines */}
                  <div className={styles.gridLines}>
                    {Array.from({ length: yAxisSteps + 1 }).map((_, i) => (
                      <div key={i} className={styles.gridLine} />
                    ))}
                  </div>
                  
                  {/* Average Line */}
                  {showAverageLine && (
                    <div 
                      className={styles.averageLine}
                      style={{ bottom: `calc(${averagePercent}% + 40px)` }}
                    />
                  )}
                  
                  {/* Bars */}
                  <div className={styles.barContainer}>
                    {barChartData.map((data, index) => {
                      const totalHeight = (data.amount / maxBarValue) * 100;
                      const isHovered = hoveredBarIndex === index;
                      const showSegments = showCategorySegments || isHovered;
                      
                      return (
                        <div 
                          key={index} 
                          className={styles.barGroup}
                          onClick={() => setSelectedBarModal(index)}
                        >
                          <div className={styles.barWrapper}>
                            {/* Bar - solid or segmented based on toggle/hover */}
                            <div 
                              className={`${styles.bar} ${showSegments ? styles.segmented : ''}`}
                              style={{ height: `${totalHeight}%` }}
                              onMouseEnter={() => setHoveredBarIndex(index)}
                              onMouseLeave={() => setHoveredBarIndex(null)}
                            >
                              {showSegments ? (
                                // Show segments on hover or when toggle is on
                                data.categories && data.categories.map((cat, catIdx) => {
                                  const segmentHeight = (cat.amount / data.amount) * 100;
                                  return (
                                    <div
                                      key={catIdx}
                                      className={styles.barSegment}
                                      style={{
                                        height: `${segmentHeight}%`,
                                        backgroundColor: cat.color
                                      }}
                                      title={`${formatCategoryName(cat.category)}: ${formatCurrency(cat.amount)}`}
                                    >
                                      {/* Show category name on larger segments */}
                                      {segmentHeight > 15 && (
                                        <span className={styles.segmentLabel}>
                                          {formatCategoryName(cat.category).split(' ')[0]}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })
                              ) : (
                                // Show solid gradient when not hovered and toggle is off
                                <div className={styles.barSolid} />
                              )}
                              
                              {/* Total amount on top */}
                              <span className={styles.barValueTop}>
                                {hideBalances ? "••" : formatCurrency(data.amount)}
                              </span>
                              
                              {/* Hover Tooltip - positioned at bar top */}
                              {isHovered && data.categories && data.categories.length > 0 && (
                                <div className={styles.barTooltip}>
                                  <div className={styles.tooltipHeader}>Category Breakdown</div>
                                  {data.categories.slice(0, 3).map((cat, idx) => (
                                    <div key={idx} className={styles.tooltipItem}>
                                      <div className={styles.tooltipColor} style={{ backgroundColor: cat.color }} />
                                      <span className={styles.tooltipCategory}>{formatCategoryName(cat.category)}</span>
                                      <span className={styles.tooltipAmount}>
                                        {hideBalances ? "••" : formatCurrency(cat.amount)} ({cat.percentage.toFixed(0)}%)
                                      </span>
                                    </div>
                                  ))}
                                  {data.categories.length > 3 && (
                                    <div className={styles.tooltipMore}>+{data.categories.length - 3} more (click for details)</div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <span className={styles.barLabel}>{data.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
      )}

      {/* Pie Chart */}
      {chartType === 'pie' && (
      <div className={styles.chartSection}>
        <div className={styles.chartHeader}>
          <div className={styles.chartTitle}>
            <PieChartIcon size={20} />
            <span>Spending by Category</span>
          </div>
        </div>
        
        <div className={styles.pieChartContainer}>
          {categorySpending.length === 0 ? (
            <div className={styles.noData}>
              <p>No spending data for this period</p>
            </div>
          ) : (
            <>
              {/* Pie Chart */}
              <div className={styles.pieChartWrapper}>
                <div className={styles.pieChart}>
                  <svg viewBox="0 0 100 100" className={styles.pieSvg}>
                    {/* Gradient definitions for better visuals */}
                    <defs>
                      <filter id="pieGlow">
                        <feGaussianBlur stdDeviation="0.5" result="coloredBlur"/>
                        <feMerge>
                          <feMergeNode in="coloredBlur"/>
                          <feMergeNode in="SourceGraphic"/>
                        </feMerge>
                      </filter>
                    </defs>
                    
                    {(() => {
                      let currentAngle = 0;
                      return categorySpending.slice(0, 10).map((cat, index) => {
                        const angle = (cat.percentage / 100) * 360;
                        const startAngle = currentAngle;
                        const endAngle = currentAngle + angle;
                        currentAngle = endAngle;
                        
                        const startRad = (startAngle - 90) * (Math.PI / 180);
                        const endRad = (endAngle - 90) * (Math.PI / 180);
                        
                        const x1 = 50 + 40 * Math.cos(startRad);
                        const y1 = 50 + 40 * Math.sin(startRad);
                        const x2 = 50 + 40 * Math.cos(endRad);
                        const y2 = 50 + 40 * Math.sin(endRad);
                        
                        const largeArc = angle > 180 ? 1 : 0;
                        
                        const pathData = `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`;
                        const isHovered = hoveredPieCategory === cat.category;
                        
                        return (
                          <path
                            key={cat.category}
                            d={pathData}
                            fill={getCategoryColor(cat.category)}
                            className={`${styles.pieSlice} ${isHovered ? styles.pieSliceHovered : ''}`}
                            onMouseEnter={() => setHoveredPieCategory(cat.category)}
                            onMouseLeave={() => setHoveredPieCategory(null)}
                            onClick={(e) => openCategoryModal(cat.category, e)}
                            filter="url(#pieGlow)"
                          />
                        );
                      });
                    })()}
                    
                    {/* Inner circle with gradient */}
                    <defs>
                      <radialGradient id="centerGradient">
                        <stop offset="0%" stopColor="var(--bg-card)" />
                        <stop offset="100%" stopColor="var(--bg-secondary)" />
                      </radialGradient>
                    </defs>
                    <circle cx="50" cy="50" r="30" fill="url(#centerGradient)" className={styles.pieCenter} />
                    
                    <text x="50" y="48" textAnchor="middle" className={styles.pieCenterAmount}>
                      {hideBalances ? "••••" : formatCurrency(totalSpending)}
                    </text>
                    <text x="50" y="57" textAnchor="middle" className={styles.pieCenterLabel}>
                      Total Spent
                    </text>
                  </svg>
                  
                  {/* Hover Info Card */}
                  {hoveredPieCategory && (() => {
                    const hoveredCat = categorySpending.find(c => c.category === hoveredPieCategory);
                    if (!hoveredCat) return null;
                    
                    return (
                      <div className={styles.pieHoverCard}>
                        <div 
                          className={styles.pieHoverCardDot}
                          style={{ background: getCategoryColor(hoveredCat.category) }}
                        />
                        <div className={styles.pieHoverCardContent}>
                          <span className={styles.pieHoverCardName}>
                            {formatCategoryName(hoveredCat.category)}
                          </span>
                          <div className={styles.pieHoverCardStats}>
                            <span className={styles.pieHoverCardAmount}>
                              {hideBalances ? "••••" : formatCurrency(hoveredCat.amount)}
                            </span>
                            <span className={styles.pieHoverCardPercent}>
                              {hoveredCat.percentage.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
              
              {/* Categories List */}
              <div className={styles.pieCategories}>
                <h4 className={styles.pieCategoriesTitle}>Categories</h4>
                <div className={styles.pieCategoriesScroll}>
                  {categorySpending.map((cat, index) => {
                    const isHovered = hoveredPieCategory === cat.category;
                    return (
                      <div 
                        key={cat.category} 
                        className={`${styles.pieCategoryItem} ${isHovered ? styles.pieCategoryItemHovered : ''}`}
                        onMouseEnter={() => setHoveredPieCategory(cat.category)}
                        onMouseLeave={() => setHoveredPieCategory(null)}
                        onClick={(e) => openCategoryModal(cat.category, e)}
                      >
                        <div className={styles.pieCategoryLeft}>
                          <div 
                            className={styles.pieCategoryDot} 
                            style={{ background: getCategoryColor(cat.category) }}
                          />
                          <div className={styles.pieCategoryInfo}>
                            <span className={styles.pieCategoryName}>{formatCategoryName(cat.category)}</span>
                            <span className={styles.pieCategoryCount}>{cat.transactionCount} transactions</span>
                          </div>
                        </div>
                        <div className={styles.pieCategoryRight}>
                          <span className={styles.pieCategoryAmount}>
                            {hideBalances ? "••••" : formatCurrency(cat.amount)}
                          </span>
                          <span className={styles.pieCategoryPercent}>{cat.percentage.toFixed(1)}%</span>
                        </div>
                        <ChevronRight size={18} className={styles.pieCategoryChevron} />
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      )}

      {/* Last Sync Info */}
      {lastSync && (
        <div className={styles.syncInfo}>
          <p>Last updated: {new Date(lastSync).toLocaleString()}</p>
        </div>
      )}

      {/* Category Transactions Modal */}
      {selectedCategoryModal && (() => {
        const categoryData = categorySpending.find(c => c.category === selectedCategoryModal);
        const isEditingModal = editingCategory === selectedCategoryModal;
        const categoryLimit = categoryData?.limit || 0;
        const categoryAmount = categoryData?.amount || 0;
        const percentageUsed = categoryLimit > 0 ? (categoryAmount / categoryLimit) * 100 : 0;
        const isOverBudget = categoryData?.isOverBudget || false;

        return (
        <>
          <div className={styles.modalBackdrop} onClick={closeCategoryModal} />
          <div className={styles.categoryModal}>
            <div className={styles.modalHeader}>
              <div className={styles.modalTitle}>
                <div 
                  className={styles.modalCategoryDot}
                  style={{
                    backgroundColor: getCategoryColorByName(selectedCategoryModal),
                  }}
                />
                <h3>{formatCategoryName(selectedCategoryModal)}</h3>
              </div>
              <button className={styles.closeModalBtn} onClick={closeCategoryModal}>
                <X size={24} />
              </button>
            </div>
            <div className={styles.modalContent}>
              {/* Budget Limit Section */}
              <div className={styles.modalLimitSection}>
                {isEditingModal ? (
                  <div className={styles.modalLimitEdit}>
                    <label className={styles.modalLimitLabel}>Monthly Limit</label>
                    <div className={styles.modalLimitInputGroup}>
                      <input
                        type="number"
                        value={editingLimit}
                        onChange={(e) => setEditingLimit(e.target.value)}
                        placeholder="Set monthly limit"
                        className={styles.modalLimitInput}
                        autoFocus
                      />
                      <button 
                        className={styles.modalSaveBtn}
                        onClick={() => saveBudgetLimit(selectedCategoryModal, parseFloat(editingLimit) || 0)}
                      >
                        <Save size={16} />
                        Save
                      </button>
                      <button 
                        className={styles.modalCancelBtn}
                        onClick={() => { setEditingCategory(null); setEditingLimit(""); }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className={styles.modalLimitDisplay}>
                    <div className={styles.modalLimitInfo}>
                      <span className={styles.modalLimitLabel}>
                        {categoryLimit > 0 ? "Monthly Limit" : "No Limit Set"}
                      </span>
                      {categoryLimit > 0 && (
                        <span className={`${styles.modalLimitAmount} ${isOverBudget ? styles.overBudget : ''}`}>
                          {formatCurrency(categoryLimit)}
                        </span>
                      )}
                    </div>
                    <button 
                      className={styles.modalEditLimitBtn}
                      onClick={() => { 
                        setEditingCategory(selectedCategoryModal); 
                        setEditingLimit(categoryLimit?.toString() || ""); 
                      }}
                    >
                      <Edit3 size={14} />
                      {categoryLimit > 0 ? "Edit Limit" : "Set Limit"}
                    </button>
                  </div>
                )}
                
                {/* Progress Bar */}
                {categoryLimit > 0 && (
                  <div className={styles.modalProgressSection}>
                    <div className={styles.modalProgressBar}>
                      <div 
                        className={`${styles.modalProgressFill} ${isOverBudget ? styles.over : ''}`}
                        style={{ width: `${Math.min(percentageUsed, 100)}%` }}
                      />
                    </div>
                    <div className={styles.modalProgressInfo}>
                      <span className={styles.modalProgressText}>
                        {hideBalances ? "••••" : formatCurrency(categoryAmount)} of {hideBalances ? "••••" : formatCurrency(categoryLimit)}
                      </span>
                      <span className={`${styles.modalProgressPercent} ${isOverBudget ? styles.over : ''}`}>
                        {percentageUsed.toFixed(1)}%
                      </span>
                    </div>
                    {isOverBudget && (
                      <div className={styles.modalOverBudgetWarning}>
                        <AlertCircle size={16} />
                        Over budget by {hideBalances ? "••••" : formatCurrency(categoryAmount - categoryLimit)}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Stats */}
              <div className={styles.modalStats}>
                <div className={styles.modalStat}>
                  <span className={styles.modalStatLabel}>Total Spent</span>
                  <span className={styles.modalStatValue}>
                    {hideBalances ? "••••" : formatCurrency(categoryAmount)}
                  </span>
                </div>
                <div className={styles.modalStat}>
                  <span className={styles.modalStatLabel}>Transactions</span>
                  <span className={styles.modalStatValue}>
                    {getCategoryTransactions(selectedCategoryModal).length}
                  </span>
                </div>
                <div className={styles.modalStat}>
                  <span className={styles.modalStatLabel}>% of Spending</span>
                  <span className={styles.modalStatValue}>
                    {(categoryData?.percentage || 0).toFixed(1)}%
                  </span>
                </div>
              </div>

              {/* Transactions List */}
              <div className={styles.modalTransactionsList}>
                {getCategoryTransactions(selectedCategoryModal).map((txn) => (
                  <div key={txn.id} className={styles.modalTransactionItem}>
                    <div className={styles.modalTransactionInfo}>
                      <span className={styles.modalTransactionName}>{txn.merchant_name || txn.name}</span>
                      <span className={styles.modalTransactionDate}>{formatDate(txn.datetime || txn.authorized_date || txn.date)}</span>
                    </div>
                    <span className={styles.modalTransactionAmount}>
                      {hideBalances ? "••••" : formatCurrency(txn.amount)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
        );
      })()}
      
      {/* Bar Details Modal */}
      {selectedBarModal !== null && barChartData[selectedBarModal] && (() => {
        const barData = barChartData[selectedBarModal];
        
        return (
          <>
            <div className={styles.modalBackdrop} onClick={() => setSelectedBarModal(null)} />
            <div className={styles.barModal}>
              {/* Header */}
              <div className={styles.modalHeader}>
                <div className={styles.modalHeaderContent}>
                  <div className={styles.modalHeaderText}>
                    <div className={styles.modalIconWrapper}>
                      <BarChart3 size={28} />
                    </div>
                    <div>
                      <h3 className={styles.modalMainTitle}>Spending Details</h3>
                      <p className={styles.barModalDate}>{barData.label}</p>
                    </div>
                  </div>
                </div>
                <button 
                  className={styles.modalCloseBtn}
                  onClick={() => setSelectedBarModal(null)}
                  aria-label="Close modal"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Content */}
              <div className={styles.barModalContent}>
                {/* Total Section */}
                <div className={styles.barModalTotal}>
                  <span className={styles.barModalTotalLabel}>Total Spending</span>
                  <span className={styles.barModalTotalAmount}>
                    {hideBalances ? "••••" : formatCurrency(barData.amount)}
                  </span>
                </div>

                {/* Categories Breakdown */}
                {barData.categories && barData.categories.length > 0 && (
                  <div className={styles.barModalCategories}>
                    <h4 className={styles.barModalSubtitle}>Categories</h4>
                    {barData.categories.map((cat, idx) => (
                      <div key={idx} className={styles.barModalCategoryItem}>
                        <div className={styles.barModalCategoryHeader}>
                          <div className={styles.barModalCategoryName}>
                            <div 
                              className={styles.barModalCategoryColor} 
                              style={{ backgroundColor: cat.color }} 
                            />
                            <span>{formatCategoryName(cat.category)}</span>
                          </div>
                          <div className={styles.barModalCategoryAmount}>
                            <span className={styles.barModalAmount}>
                              {hideBalances ? "••••" : formatCurrency(cat.amount)}
                            </span>
                            <span className={styles.barModalPercentage}>
                              {cat.percentage.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                        <div className={styles.barModalCategoryBar}>
                          <div 
                            className={styles.barModalCategoryBarFill}
                            style={{ 
                              width: `${cat.percentage}%`,
                              backgroundColor: cat.color 
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        );
      })()}
    </div>
  );
};

export default Budget;
